<script lang="ts" setup>
import type { VariantProps } from 'class-variance-authority';

import FavoriteStarPulse from '@/components/FavoriteFooterButton/FavoriteStarPulse.vue';
import {
	calculatePulseAnimationTotalDuration,
	useFavoriteStarPulse
} from '@/composables/useFavoriteStarPulse';
import { cva } from 'class-variance-authority';

const favoriteButtonVariants = cva(
	'inline-flex h-11 items-center justify-center gap-2 rounded-[2px] border px-3 text-sm font-medium leading-5 transition-[background-color,border-color,color] duration-500 ease-out cursor-pointer',
	{
		variants: {
			state: {
				default:
					'border-white-dark-4 bg-transparent text-primary hover:border-primary',
				selected:
					'border-beige bg-beige text-primary hover:border-beige-light-2 hover:bg-beige-light-2',
				fading:
					'border-white-dark-4 bg-white text-primary hover:border-primary',
				'fading-selected':
					'border-beige bg-beige text-primary hover:border-beige-light-2 hover:bg-beige-light-2'
			},
			fadeAnimation: {
				enabled: '',
				disabled: ''
			}
		},
		defaultVariants: {
			state: 'default',
			fadeAnimation: 'disabled'
		}
	}
);

type FavoriteButtonVariants = VariantProps<typeof favoriteButtonVariants>;

const props = withDefaults(
	defineProps<{
		isSelected: boolean;
		isEnabledForPulse?: boolean;
		showLabel?: boolean;
		showLabelOnMobile?: boolean;
		fadeAnimation?: FavoriteButtonVariants['fadeAnimation'];
	}>(),
	{
		isEnabledForPulse: false,
		showLabel: true,
		showLabelOnMobile: false,
		fadeAnimation: 'disabled'
	}
);

const emit = defineEmits<{ (e: 'toggle'): void }>();

const { isMainPulsing, isBackgroundPulsing } = useFavoriteStarPulse({
	isEnabled: computed(() => props.isEnabledForPulse),
	isSelected: computed(() => props.isSelected)
});

// Background fade animation - triggers during pulse (only if fadeAnimation is enabled)
const isBackgroundFading = ref(false);

let fadeTimeoutId: ReturnType<typeof setTimeout> | undefined;

// Watch for pulse states to trigger background fade
watch(
	[isMainPulsing, isBackgroundPulsing],
	([mainPulsing, backgroundPulsing]) => {
		if (props.fadeAnimation !== 'enabled') return;

		// Enable background when any pulse starts
		if (mainPulsing || backgroundPulsing) {
			if (!isBackgroundFading.value) {
				isBackgroundFading.value = true;

				if (fadeTimeoutId) {
					clearTimeout(fadeTimeoutId);
				}

				const totalAnimationDuration = calculatePulseAnimationTotalDuration();
				fadeTimeoutId = setTimeout(() => {
					isBackgroundFading.value = false;
				}, totalAnimationDuration);
			}
		}
	}
);

onUnmounted(() => {
	if (fadeTimeoutId) {
		clearTimeout(fadeTimeoutId);
	}
});

// Computed for button state based on current conditions
const buttonState = computed(() => {
	if (props.fadeAnimation === 'enabled' && isBackgroundFading.value) {
		return props.isSelected ? 'fading-selected' : 'fading';
	}
	return props.isSelected ? 'selected' : 'default';
});

const labelText = computed(() => (props.isSelected ? 'Salvo' : 'Salvar'));
const labelVisibilityClass = computed(() =>
	props.showLabelOnMobile ? 'inline' : 'hidden sm:inline'
);

const ariaLabel = computed<string>(() =>
	props.isSelected ? 'Remover da minha coleção' : 'Adicionar à minha coleção'
);
</script>

<template>
	<button
		type="button"
		:class="
			favoriteButtonVariants({
				state: buttonState,
				fadeAnimation: props.fadeAnimation
			})
		"
		:aria-label="ariaLabel"
		:aria-pressed="isSelected"
		@click.stop.prevent="emit('toggle')"
	>
		<FavoriteStarPulse
			:is-selected="props.isSelected"
			:is-main-pulsing="isMainPulsing"
			:is-background-pulsing="isBackgroundPulsing"
		/>
		<span
			v-if="props.showLabel"
			:class="['text-primary', labelVisibilityClass]"
		>
			{{ labelText }}
		</span>
	</button>
</template>
